import { Skeleton, Stack, Typography } from '@mui/material';
import { Pencil } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { ListingMandateCardFragment } from '@graphql/__generated__/graphql';
import { CardHeaderIconActions } from '@modules/_shared/components/CardHeaderIconActions';
import {
  listingCardBodyTextSx,
  listingCardSecondaryTextSx,
} from '@modules/_shared/components/listing-overview/listing-overview-styles';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { fromDateStringToUTC } from '@modules/_shared/utils/dates';
import { toHumanReadableDate } from '@modules/_shared/utils/formatting';
import { getExpectedTimeToSellLabel } from '@modules/_shared/utils/general-labels';
import {
  type IntlLocale,
  type Translate,
  useLocale,
} from '@shared/hooks/locale';
import { type Currency } from '@shared/LocaleContext';
import { formatPrice } from '@shared/utils/format-price';

export const LISTING_MANDATE_CARD_FRAGMENT = gql(/* GraphQL */ `
  fragment ListingMandateCard on lots {
    origin {
      id
      label
      name
    }
    currency
    commission_rate
    fixed_fee
    is_exclusive
    mandate_type
    expected_time_to_sell
    signed_at
    property {
      id
      country_code
    }
  }
`);

interface ListingMandateCardProps {
  listing?: ListingMandateCardFragment | null;
  isLoading?: boolean;
  openedDefault?: boolean;
  onEdit?: () => void;
}

const getMandateAmountOrPercentage = (
  mandate: ListingMandateCardFragment,
  t: Translate,
  locale: IntlLocale,
  currency: Currency,
) => {
  if (mandate.mandate_type == null) {
    return <i>{t('Set a mandate type')}</i>;
  }

  if (mandate.mandate_type === 'commission_based') {
    return mandate.commission_rate == null ? (
      <i>{t('Set a commission rate')}</i>
    ) : (
      `${(mandate.commission_rate * 100).toLocaleString(locale, {
        maximumFractionDigits: 1,
      })}%`
    );
  }

  return mandate.fixed_fee == null ? (
    <i>{t('Set a fixed fee')}</i>
  ) : (
    formatPrice(mandate.fixed_fee, locale, currency)
  );
};

export const ListingMandateCardSkeleton: React.FC<{
  openedDefault?: boolean;
}> = ({ openedDefault }) => {
  const { t } = useLocale();
  return (
    <ListingAccordion title={t('Mandate')} defaultExpanded={openedDefault}>
      <Stack spacing={0.5}>
        <Typography sx={listingCardBodyTextSx}>
          <Skeleton width={150} />
        </Typography>
        <Stack spacing={0.25}>
          <Typography sx={listingCardSecondaryTextSx}>
            <Skeleton width={250} />
          </Typography>
          <Typography sx={listingCardSecondaryTextSx}>
            <Skeleton width={180} />
          </Typography>
        </Stack>
      </Stack>
    </ListingAccordion>
  );
};

export const ListingMandateCard: React.FC<ListingMandateCardProps> = ({
  listing,
  openedDefault,
  onEdit,
  isLoading = false,
}) => {
  const { t, locale } = useLocale();

  if (isLoading) {
    return <ListingMandateCardSkeleton openedDefault={openedDefault} />;
  }

  const currencyToFormat = (listing?.currency ??
    (listing?.property.country_code === 'CH' ? 'CHF' : 'EUR')) as Currency;

  return (
    <ListingAccordion
      title={t('Mandate')}
      defaultExpanded={openedDefault}
      headerAction={
        onEdit != null ? (
          <CardHeaderIconActions
            actions={[
              { label: t('Edit'), icon: <Pencil size={16} />, onClick: onEdit },
            ]}
          />
        ) : undefined
      }
    >
      {listing != null && (
        <Stack
          spacing={0.5}
          sx={theme => ({
            '& .MuiTypography-root': theme.mixins.truncate(),
          })}
        >
          <Typography sx={listingCardBodyTextSx}>
            {getMandateAmountOrPercentage(listing, t, locale, currencyToFormat)}
            {' • '}
            {listing.is_exclusive == null ? (
              <i>{t('Set exclusivity')}</i>
            ) : listing.is_exclusive ? (
              t('Exclusive')
            ) : (
              t('Non-exclusive')
            )}
          </Typography>
          <Stack spacing={0.25}>
            <Typography sx={listingCardSecondaryTextSx}>
              {listing.signed_at != null
                ? t('Signed {{date}}', {
                    date: toHumanReadableDate(
                      locale,
                      fromDateStringToUTC(listing.signed_at).getTime(),
                      {
                        month: 'numeric',
                      },
                    ),
                  })
                : t('Not signed yet')}
              {' • '}
              {getExpectedTimeToSellLabel(listing.expected_time_to_sell, t) ??
                t('Set expected time to sell')}
            </Typography>
            <Typography sx={listingCardSecondaryTextSx}>
              {t('Mandate origin: {{origin}}', {
                origin:
                  listing.origin?.label ?? listing.origin?.name ?? t('Unknown'),
              })}
            </Typography>
          </Stack>
        </Stack>
      )}
    </ListingAccordion>
  );
};
